import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { z } from 'zod';
import { RiskReactionConditionsRequestSchema, } from '@wit-support/admin-contracts';
import { Dialog, DialogContent, DialogDescription, DialogTitle, FormField, Label, toast, } from '@wit-support/ui';
import { Actions, SubmitButton, ZodField, ZodForm, useZodFormContext, CancelButton, } from '@wit-support/ui-forms';
import { createRule, updateRule } from '../api.js';
import { ReactionConditionEditor } from './reaction-condition-editor.js';
/**
 * `respondAlert: true` без `refund: true` — правило отвечает провайдеру
 * решением («Resolved»), не возвращая клиенту деньги. Сервер (см.
 * `validateRespondAlertNeedsRefundOrConfirm` в `@wit-support/admin-contracts`)
 * требует явного `confirmNoRefundResponse: true` для этой комбинации; форма
 * ставит ТОТ ЖЕ гвард клиентской стороной (через `superRefine` ниже) —
 * оператор обязан увидеть предупреждение раньше, чем 422 от сервера.
 */
const RuleFormSchema = z
    .object({
    name: z
        .string()
        .trim()
        .min(1, 'Обязательное поле')
        .max(200, 'Не длиннее 200 символов')
        .meta({ label: 'Название' }),
    refund: z.boolean().meta({ label: 'Возврат' }),
    cancelSubscription: z.boolean().meta({ label: 'Отмена подписки' }),
    respondAlert: z.boolean().meta({ label: 'Ответ провайдеру («Resolved»)' }),
    /** Одноразовый флаг подтверждения — НЕ сохраняется в самом правиле. */
    confirmNoRefundResponse: z.boolean().meta({
        label: 'Ответить Resolved без возврата — вы уверены?',
    }),
})
    .superRefine((v, ctx) => {
    if (v.respondAlert && !v.refund && !v.confirmNoRefundResponse) {
        ctx.addIssue({
            code: 'custom',
            path: ['confirmNoRefundResponse'],
            message: 'Правило отвечает провайдеру решением БЕЗ возврата денег клиенту. Подтвердите чекбокс либо включите возврат.',
        });
    }
});
/**
 * Шаги + подтверждение. Отдельным компонентом, потому что видимость
 * подтверждающего чекбокса и его сброс завязаны на ДВА поля формы сразу
 * (`respondAlert` и `refund`) — прочитать их можно только изнутри `ZodForm`
 * (тот же приём, что `ActionFields` в `routes/case.tsx`).
 */
function StepsFields() {
    const { form } = useZodFormContext();
    const respondAlert = Boolean(form.watch('respondAlert'));
    const refund = Boolean(form.watch('refund'));
    const needsConfirm = respondAlert && !refund;
    // Уходящий из вида чекбокс обязан и ЗНАЧИТЬ «нет»: оператор мог включить
    // его до того, как выключил возврат обратно, и оставшееся включённым
    // подтверждение относилось бы уже к другой комбинации степов.
    useEffect(() => {
        if (!needsConfirm && form.getValues('confirmNoRefundResponse') === true) {
            form.setValue('confirmNoRefundResponse', false);
        }
    }, [needsConfirm, form]);
    return (_jsxs("div", { className: "flex flex-col gap-3", children: [_jsx(ZodField, { name: "refund" }), _jsx(ZodField, { name: "cancelSubscription" }), _jsx(ZodField, { name: "respondAlert" }), needsConfirm ? (_jsxs("div", { className: "rounded-[var(--radius-3)] border border-[var(--color-state-warning-fg)] bg-[var(--color-state-warning-bg)] p-3", children: [_jsx("p", { className: "mb-2 text-sm text-[var(--color-state-warning-fg)]", children: "\u041F\u0440\u0430\u0432\u0438\u043B\u043E \u043E\u0442\u0432\u0435\u0447\u0430\u0435\u0442 \u043F\u0440\u043E\u0432\u0430\u0439\u0434\u0435\u0440\u0443 \u0440\u0435\u0448\u0435\u043D\u0438\u0435\u043C \u00ABResolved\u00BB, \u043D\u0435 \u0432\u043E\u0437\u0432\u0440\u0430\u0449\u0430\u044F \u0434\u0435\u043D\u044C\u0433\u0438 \u043A\u043B\u0438\u0435\u043D\u0442\u0443. \u041E\u0431\u044B\u0447\u043D\u043E \u044D\u0442\u043E \u0437\u0430\u0431\u044B\u0442\u044B\u0439 \u0447\u0435\u043A\u0431\u043E\u043A\u0441 \u0432\u043E\u0437\u0432\u0440\u0430\u0442\u0430, \u0430 \u043D\u0435 \u043E\u0441\u043E\u0437\u043D\u0430\u043D\u043D\u043E\u0435 \u0440\u0435\u0448\u0435\u043D\u0438\u0435." }), _jsx(ZodField, { name: "confirmNoRefundResponse" })] })) : null] }));
}
/**
 * Диалог создания/правки правила реакции. Условие (`RiskReactionExpr`)
 * редактируется ВНЕ `ZodForm` — тем же приёмом, что триггер пайплайна
 * (`admin-module-pipelines/src/client/forms/pipeline-dialog.tsx`, FP-12):
 * дерево условий проходит через транзитно-невалидные состояния (пустая
 * группа, недо-выбранное поле), а `ZodForm` валидирует единственную плоскую
 * форму на каждое нажатие клавиши. Условие уезжает на провод из локального
 * состояния в момент сабмита формы.
 */
export function RuleDialog({ rule, open, onOpenChange, onSaved }) {
    const [conditions, setConditions] = useState(rule?.conditions ?? { type: 'always' });
    /**
     * First RU issue message from the SAME schema the server runs
     * (`RiskReactionConditionsRequestSchema` — the conditions half of
     * `CreateRiskReactionRuleRequest`/`UpdateRiskReactionRuleRequest`), surfaced
     * next to the condition editor rather than as a generic toast: the editor
     * itself routinely passes through transiently-invalid states (a freshly
     * added `field` leaf with no `value` yet), and only a submit attempt should
     * ever surface that as an error — the same reasoning `pipeline-dialog.tsx`
     * (`FP-12`) applies to `TriggerExpr`.
     */
    const [conditionsError, setConditionsError] = useState(null);
    const mut = useMutation({
        mutationFn: (values) => {
            const steps = {
                refund: values.refund,
                cancelSubscription: values.cancelSubscription,
                respondAlert: values.respondAlert,
            };
            return rule
                ? updateRule(rule.id, {
                    name: values.name,
                    conditions,
                    steps,
                    confirmNoRefundResponse: values.confirmNoRefundResponse,
                })
                : createRule({
                    name: values.name,
                    conditions,
                    steps,
                    confirmNoRefundResponse: values.confirmNoRefundResponse,
                });
        },
        onSuccess: () => {
            toast.success(rule ? 'Правило обновлено' : 'Правило добавлено');
            onSaved();
        },
        onError: (err) => toast.error(err.message),
    });
    return (_jsx(Dialog, { open: open, onOpenChange: (v) => (!v ? onOpenChange(false) : null), children: _jsxs(DialogContent, { size: "lg", children: [_jsx(DialogTitle, { children: rule ? 'Изменить правило' : 'Добавить правило' }), _jsx(DialogDescription, { children: "\u041A\u0435\u0439\u0441\u044B \u043F\u0440\u043E\u0432\u0435\u0440\u044F\u044E\u0442\u0441\u044F \u043F\u0440\u0430\u0432\u0438\u043B\u0430\u043C\u0438 \u043F\u043E \u043F\u043E\u0440\u044F\u0434\u043A\u0443 (\u0441\u043C. \u0441\u043F\u0438\u0441\u043E\u043A) \u2014 \u0432\u044B\u0438\u0433\u0440\u044B\u0432\u0430\u0435\u0442 \u041F\u0415\u0420\u0412\u041E\u0415 \u0432\u043A\u043B\u044E\u0447\u0451\u043D\u043D\u043E\u0435 \u0441\u043E\u0432\u043F\u0430\u0432\u0448\u0435\u0435 \u043F\u0440\u0430\u0432\u0438\u043B\u043E. \u041F\u043E\u0440\u044F\u0434\u043E\u043A \u0437\u0430\u0434\u0430\u0451\u0442\u0441\u044F \u0441\u0442\u0440\u0435\u043B\u043A\u0430\u043C\u0438 \u0432 \u0441\u043F\u0438\u0441\u043A\u0435, \u043D\u0435 \u0437\u0434\u0435\u0441\u044C." }), _jsxs(ZodForm, { schema: RuleFormSchema, defaultValues: {
                        name: rule?.name ?? '',
                        refund: rule?.steps.refund ?? false,
                        cancelSubscription: rule?.steps.cancelSubscription ?? false,
                        respondAlert: rule?.steps.respondAlert ?? false,
                        confirmNoRefundResponse: false,
                    }, onSubmit: (values) => {
                        // Same schema the server runs on `conditions` — block the submit
                        // and surface the FIRST RU issue right next to the editor instead
                        // of letting an invalid tree reach the network only to bounce
                        // back as a generic 422.
                        const parsedConditions = RiskReactionConditionsRequestSchema.safeParse(conditions);
                        if (!parsedConditions.success) {
                            setConditionsError(parsedConditions.error.issues[0]?.message ?? 'Некорректное условие');
                            return;
                        }
                        setConditionsError(null);
                        mut.mutate(values);
                    }, children: [_jsx(ZodField, { name: "name" }), _jsxs("div", { className: "flex flex-col gap-2", children: [_jsx(Label, { children: "\u0423\u0441\u043B\u043E\u0432\u0438\u0435" }), _jsx(ReactionConditionEditor, { value: conditions, onChange: (next) => {
                                        setConditions(next);
                                        setConditionsError(null);
                                    } }), conditionsError ? _jsx(FormField.Error, { children: conditionsError }) : null] }), _jsx(StepsFields, {}), _jsxs(Actions, { children: [_jsx(CancelButton, { onClick: () => onOpenChange(false) }), _jsx(SubmitButton, { pendingLabel: "\u0421\u043E\u0445\u0440\u0430\u043D\u044F\u0435\u043C\u2026", children: rule ? 'Сохранить' : 'Добавить' })] })] })] }) }));
}
//# sourceMappingURL=rule-dialog.js.map